// Pure race logic for the 3D tracks (no three.js here, so it's unit-tested):
// feed types, where each car sits on the lap, pit stop scheduling, the
// tower's focus toggle, the class filter and the day/night cycle.

export type Car = {
  id: number
  slug: string | null
  name: string
  team: string
  number: number
  livery: string
  paint?: { primary: string; secondary: string; accent: string; pattern: string; number: number | string; name: string; logo?: string | null; logo_wide?: boolean }
  sample: boolean
  url: string | null
  rel?: string | null
  tagline?: string
  category?: string
  description?: string | null
  driver?: Record<string, string | number>
  // Grand Prix
  votes?: number
  gap?: number
  slot?: number
  kind?: string
  position?: number
  // Endurance
  car_class?: string
  class_name?: string
  class_color?: string
  overall?: number
  class_position?: number
  km?: number
  pace?: number
  fuel?: number
  pit_stop_at?: string | null
}

// `art`: the image the sponsor handed in for its sign, shown instead of the painted name.
export type Sponsor = { name: string; tagline: string | null; color: string; url: string | null; logo?: string | null; art?: string | null }
export type Billboard = { spot: number; name: string; tagline: string | null; color: string; url: string | null; logo?: string | null; art?: string | null; sample: boolean }

export type Feed = {
  mode: 'grand_prix' | 'endurance'
  sample: boolean
  cars: Car[]
  billboards: Billboard[]
  classes?: { id: string; name: string; color: string; sponsor?: Sponsor | null }[]
  // The three big gantry banners (null = open): the round's on the Grand Prix, the month's on Endurance.
  banners?: (Sponsor | null)[]
  // Endurance, by month: the footbridge and the Ferris wheel banners.
  bridge?: Sponsor | null
  ferris?: Sponsor | null
  round?: { id: number; title: string; state: string; ends_at: string; sponsor: { name: string; url: string; color: string } | null }
}

export const wrap = (u: number) => ((u % 1) + 1) % 1

// How far behind the leader each car runs, as a fraction of the lap.
//   Grand Prix: a fixed spacing by running order plus the vote gap.
//   Endurance:  the distance gap folded onto one lap (so lapped cars spread
//               around the circuit), ordered by overall position.
export function lapOffsets(feed: Feed, lapKm = 13.6): Map<number, number> {
  const offsets = new Map<number, number>()
  if (feed.mode === 'grand_prix') {
    const maxGap = Math.max(1, ...feed.cars.map((car) => car.gap ?? 0))
    feed.cars.forEach((car, index) => offsets.set(car.id, Math.min(0.85, index * 0.014 + ((car.gap ?? 0) / maxGap) * 0.3)))
  } else {
    const leader = Math.max(0, ...feed.cars.map((car) => car.km ?? 0))
    feed.cars.forEach((car, index) => {
      const behind = (leader - (car.km ?? 0)) / lapKm
      offsets.set(car.id, index === 0 ? 0 : Math.max(0.01 * index, wrap(behind)))
    })
  }
  return offsets
}

// Laps per second at the base pace, and each car's relative speed.
export const BASE_LAPS_PER_SECOND = { grand_prix: 1 / 26, endurance: 1 / 45 }

export function speedFactor(car: Car, mode: Feed['mode']): number {
  if (mode === 'grand_prix') return 1
  const pace = car.pace ?? 1
  return 0.9 + Math.min(0.25, pace * 0.08)
}

// Every car pits now and then; in Endurance a car with low fuel or a fresh
// pit stop (an update shipped in the last 3 days) pits more often.
export function pitsOnLap(car: Car, lap: number, mode: Feed['mode'], now = Date.now()): boolean {
  if (lap < 1) return false
  if (mode === 'grand_prix') return (lap + car.id) % 9 === 0
  const fresh = car.pit_stop_at ? now - Date.parse(car.pit_stop_at) < 3 * 86_400_000 : false
  const every = fresh ? 2 : (car.fuel ?? 1) < 0.6 ? 3 : 5
  return (lap + Math.abs(car.id)) % every === 0
}

// Tower clicks: the first click on a name follows the car, the next shows its
// pit box, and so on. A different name starts again on its car.
export type CameraMode = 'broadcast' | 'chase' | 'free'
export type Focus = { kind: CameraMode } | { kind: 'car' | 'pit'; id: number }

export function toggleFocus(focus: Focus, id: number): Focus {
  if (focus.kind === 'car' && focus.id === id) return { kind: 'pit', id }
  return { kind: 'car', id }
}

export function focusedId(focus: Focus): number | null {
  return focus.kind === 'car' || focus.kind === 'pit' ? focus.id : null
}

// Endurance class filter: which tower rows to show, renumbered in class.
export function filterByClass(cars: Car[], classId: string): Car[] {
  return classId === 'all' ? cars : cars.filter((car) => car.car_class === classId)
}

// Endurance day/night: one slow cycle. 0 = noon, 0.5 = midnight.
// Returns the daylight (0..1) and whether lights should be on.
export function daylight(phase: number): { light: number; night: boolean } {
  const light = (Math.cos(wrap(phase) * Math.PI * 2) + 1) / 2
  return { light, night: light < 0.35 }
}

// "+42 votes" / "LEADER", "GT P3".
export function gapLabel(car: Car, index: number): string {
  if (index === 0) return 'LEADER'
  const gap = car.gap ?? 0
  return `+${gap} ${gap === 1 ? 'vote' : 'votes'}`
}

export function classLabel(car: Car): string {
  return `${car.class_name ?? ''} P${car.class_position ?? ''}`.trim()
}
